import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Briefcase, DollarSign, Clock, Calendar, Eye, Receipt, FileText, Calculator } from 'lucide-react';
import axios from 'axios';

const MyDashboard = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [employeeConfig, setEmployeeConfig] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [paystubs, setPaystubs] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [taxSettings, setTaxSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [authError, setAuthError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'hours', 'paystubs', 'timesheets', or 'taxes'
  const [hoursFilter, setHoursFilter] = useState('current_month'); // Filter for hours tab
  const [paystubsFilter, setPaystubsFilter] = useState('current_month'); // Filter for paystubs tab
  const [timesheetsFilter, setTimesheetsFilter] = useState('current_month'); // Filter for timesheets tab

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // State names lookup
  const stateNames = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California', 'CO': 'Colorado',
    'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
    'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana',
    'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
    'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
    'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota',
    'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington',
    'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  // Filing status labels
  const filingStatusLabels = {
    'single': 'Single',
    'married_jointly': 'Married Filing Jointly',
    'married_separately': 'Married Filing Separately',
    'head_of_household': 'Head of Household'
  };

  // Authentication check - only allow employees to view their own dashboard
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    // Allow actual employees (manager, technician, user) and owners who are also employees
    if (user && (!['admin', 'owner'].includes(user.role) || (user.role === 'owner' && user.is_employee_also))) {
      setAuthError(null);
      setPageLoading(false);
    } else if (user && (user.role === 'admin' || (user.role === 'owner' && !user.is_employee_also))) {
      setAuthError('Access denied. Management-only roles cannot access employee dashboard.');
      setPageLoading(false);
    } else if (!token) {
      setAuthError('Please log in to view your dashboard.');
      setPageLoading(false);
    }
  }, [user, token, authLoading]);

  // Fetch data when page loads
  useEffect(() => {
    if (!pageLoading && user && user.id && (!['admin', 'owner'].includes(user.role) || (user.role === 'owner' && user.is_employee_also))) {
      fetchEmployeeData();
    }
  }, [pageLoading, user]);

  // Fetch hours when hours tab is selected
  useEffect(() => {
    if (activeTab === 'hours' && !pageLoading && user && user.id) {
      fetchTimeEntries();
    }
  }, [activeTab, hoursFilter, pageLoading, user]);

  // Clear error messages when switching tabs
  useEffect(() => {
    setMessage({ type: '', text: '' });
  }, [activeTab]);

  // Fetch paystubs when paystubs tab is selected
  useEffect(() => {
    if (activeTab === 'paystubs' && !pageLoading && user && user.id) {
      fetchPaystubs();
    }
  }, [activeTab, pageLoading, user]);

  // Fetch timesheets when timesheets tab is selected
  useEffect(() => {
    if (activeTab === 'timesheets' && !pageLoading && user && user.id) {
      fetchTimesheets();
    }
  }, [activeTab, pageLoading, user]);

  // Fetch tax settings when taxes tab is selected
  useEffect(() => {
    if (activeTab === 'taxes' && !pageLoading && user && user.id) {
      fetchTaxSettings();
    }
  }, [activeTab, pageLoading, user]);

  const fetchTaxSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tax-settings/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTaxSettings(response.data);
    } catch (error) {
      console.error('Error fetching tax settings:', error);
      if (error.response?.status === 404) {
        // No tax settings found
        setTaxSettings(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      // Fetch employee details
      const employeeResponse = await axios.get(`${API_BASE_URL}/api/employees/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployee(employeeResponse.data);

      // Fetch employee config
      const configResponse = await axios.get(`${API_BASE_URL}/api/employee-configs/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployeeConfig(configResponse.data);

    } catch (err) {
      console.error('Error fetching employee data:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to fetch employee data' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      console.log('=== MYDASHBOARD DEBUG START ===');
      console.log('Fetching time entries for user:', user.id, 'Email:', user.email, 'Role:', user.role);
      console.log('Current filter:', hoursFilter);
      console.log('API Base URL:', API_BASE_URL);
      console.log('Token exists:', !!token);
      
      // Calculate date range based on filter
      let startDate, endDate;
      const today = new Date().toISOString().split('T')[0];
      
      switch (hoursFilter) {
        case 'latest_20':
          // Latest 20 days - should be 20 days ago, not 6 months
          startDate = new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          endDate = today;
          break;
        case 'current_month':
          const now = new Date();
          startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          endDate = today;
          break;
        case 'last_30_days':
          startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          endDate = today;
          break;
        case 'september_2025':
          startDate = '2025-09-01';
          endDate = '2025-09-30';
          break;
        default:
          startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          endDate = today;
      }

      console.log('Date range calculated:', { startDate, endDate, filter: hoursFilter });

      let entries = [];
      
      // Try the timesheet API endpoints
      try {
        console.log('=== TRYING MY-HOURS ENDPOINT ===');
        const myHoursUrl = `${API_BASE_URL}/api/timesheet/my-hours?start_date=${startDate}&end_date=${endDate}`;
        console.log('API URL:', myHoursUrl);
        
        const response = await axios.get(myHoursUrl, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('My-hours API response status:', response.status);
        console.log('My-hours API response data:', response.data);
        console.log('Number of entries returned:', response.data?.length || 0);
        
        entries = response.data || [];
        
      } catch (err) {
        console.log('=== MY-HOURS ENDPOINT FAILED ===');
        console.log('Error status:', err.response?.status);
        console.log('Error data:', err.response?.data);
        console.log('Full error:', err.message);
        
        // Try reports endpoint as fallback
        try {
          console.log('=== TRYING REPORTS ENDPOINT AS FALLBACK ===');
          const reportsUrl = `${API_BASE_URL}/api/timesheet/reports`;
          console.log('Reports URL:', reportsUrl);
          
          const reportsResponse = await axios.get(reportsUrl, {
            headers: { 'Authorization': `Bearer ${token}` },
            params: {
              start_date: startDate,
              end_date: endDate,
              user_id: user.id
            }
          });

          console.log('Reports API response status:', reportsResponse.status);
          console.log('Reports API response data:', reportsResponse.data);
          
          // Extract entries from reports response
          const reports = reportsResponse.data.reports || [];
          console.log('Number of reports:', reports.length);
          
          const userReport = reports.find(r => r.user_id === user.id);
          console.log('User report found:', !!userReport);
          
          if (userReport) {
            entries = userReport.entries || [];
            console.log('Entries from user report:', entries.length);
          }
          
        } catch (reportsErr) {
          console.log('=== REPORTS ENDPOINT ALSO FAILED ===');
          console.log('Reports error status:', reportsErr.response?.status);
          console.log('Reports error data:', reportsErr.response?.data);
          throw err; // Throw original my-hours error
        }
      }
      
      console.log('=== PROCESSING RETRIEVED ENTRIES ===');
      console.log('Total entries retrieved:', entries.length);

      // Log all entries for debugging
      if (entries.length > 0) {
        console.log('All entries details:');
        entries.forEach((entry, index) => {
          console.log(`Entry ${index + 1}:`, {
            id: entry.id,
            user_id: entry.user_id,
            clock_in_time: entry.clock_in_time,
            clock_out_time: entry.clock_out_time,
            status: entry.status,
            total_hours: entry.total_hours
          });
        });
      } else {
        console.log('❌ NO ENTRIES RETRIEVED FROM API');
      }
      
      // Filter and sort entries - handle both completed and active entries
      const filteredEntries = entries.filter(entry => {
        const hasClockIn = !!entry.clock_in_time;
        
        // For completed entries, require total_hours > 0
        const isCompleted = entry.status !== 'active' && entry.total_hours && entry.total_hours > 0;
        
        // For active entries (currently working), show them even without total_hours
        const isActive = entry.status === 'active' && hasClockIn;
        
        const isValidEntry = hasClockIn && (isCompleted || isActive);
        
        console.log(`Entry ${entry.id}: hasClockIn=${hasClockIn}, status="${entry.status}", total_hours=${entry.total_hours}, isCompleted=${isCompleted}, isActive=${isActive}, isValidEntry=${isValidEntry}`);
        return isValidEntry;
      });

      console.log('Entries after filtering:', filteredEntries.length);

      // Sort by most recent
      const sortedEntries = filteredEntries.sort((a, b) => new Date(b.clock_in_time) - new Date(a.clock_in_time));

      // Limit to 20 entries if using latest_20 filter
      const finalEntries = hoursFilter === 'latest_20' ? sortedEntries.slice(0, 20) : sortedEntries;

      console.log('Final entries to display:', finalEntries.length);
      console.log('=== MYDASHBOARD DEBUG END ===');

      setTimeEntries(finalEntries);
      
      // Clear any previous error messages when entries are successfully fetched
      setMessage({ type: '', text: '' });
      
    } catch (err) {
      console.error('=== CRITICAL ERROR IN FETCHENTRIES ===');
      console.error('Error message:', err.message);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      setMessage({ 
        type: 'error', 
        text: `Failed to fetch time entries. Error: ${err.response?.data?.detail || err.message}. Check browser console for details.` 
      });
    }
  };

  const fetchPaystubs = async () => {
    try {
      // Fetch paystubs for this employee from published posts
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        const publishedPosts = response.data.posts || response.data;
        
        // Filter for paystub posts for this specific employee
        const employeePaystubs = publishedPosts.filter(post => {
          const isPaystubPost = post.platforms?.includes('paystub') || 
                              post.post_type === 'paystub' ||
                              post.agent_type === 'paystub_agent' ||
                              (post.agent_name && post.agent_name.toLowerCase().includes('paystub'));
          
          if (!isPaystubPost) return false;
          
          // Check if this employee is in the paystub_data.employee_paystubs
          const paystubData = post.paystub_data || {};
          const employeePaystubsList = paystubData.employee_paystubs || [];
          const isForThisEmployee = employeePaystubsList.some(emp => emp.user_id === user.id);
          
          // Also check the old method for backward compatibility
          const isForThisEmployeeOld = post.selected_employees?.includes(user.id) ||
                                      post.employee_id === user.id;
          
          return isForThisEmployee || isForThisEmployeeOld;
        });

        // Sort by created/published date (newest first)
        const sortedPaystubs = employeePaystubs.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });

        setPaystubs(sortedPaystubs);
        // Clear any previous error messages when data loads successfully
        setMessage({ type: '', text: '' });
      }
    } catch (error) {
      console.error('Error fetching paystubs:', error);
      // Only show error message for actual API failures, not empty data
      if (error.response && error.response.status >= 400) {
        setMessage({ 
          type: 'error', 
          text: 'Failed to load paystubs. Please try again later.' 
        });
      }
    }
  };

  const fetchTimesheets = async () => {
    try {
      // Fetch published timesheet posts
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?agent_type=time_sheet&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const timesheetPosts = response.data.posts || [];
      
      // Filter timesheets that include this employee
      const relevantTimesheets = timesheetPosts.filter(post => {
        if (post.agent_type === 'time_sheet' && post.timesheet_data) {
          const employeeData = post.timesheet_data.employee_data || [];
          return employeeData.some(emp => emp.user_id === user.id);
        }
        return false;
      });

      setTimesheets(relevantTimesheets);
      // Clear any previous error messages when data loads successfully
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      // Only show error message for actual API failures, not empty data
      if (error.response && error.response.status >= 400) {
        setMessage({ 
          type: 'error', 
          text: 'Failed to load timesheets. Please try again later.' 
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    
    // Handle full datetime strings
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(timeString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const totalMinutes = Math.round(hours * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="h-12 w-12 text-red-500 mx-auto mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!employee || !employeeConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Employee data not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {employee?.full_name ? `${employee.full_name}'s Dashboard` : 'My Dashboard'}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <div className="h-5 w-5 text-red-400">⚠️</div>
                    ) : (
                      <div className="h-5 w-5 text-green-400">✅</div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>My Details</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('hours')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'hours'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>My Hours</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('paystubs')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'paystubs'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Receipt className="h-4 w-4" />
                      <span>My Paystubs</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('timesheets')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'timesheets'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span>My Timesheets</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('taxes')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'taxes'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Calculator className="h-4 w-4" />
                      <span>My Taxes</span>
                    </div>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'details' ? (
                  // My Details Tab Content (Non-editable)
                  <div className="space-y-6">
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name
                          </label>
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{employee.full_name}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                          </label>
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{employee.email}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Role
                          </label>
                          <div className="flex items-center space-x-2">
                            <Briefcase className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{capitalizeFirstLetter(employee.role)}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Joining Date
                          </label>
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">
                              {employee.joining_date 
                                ? new Date(employee.joining_date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })
                                : 'Not specified'
                              }
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            employeeConfig.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {employeeConfig.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hourly Rate
                          </label>
                          <div className="flex items-center space-x-2">
                            <DollarSign className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{formatCurrency(employeeConfig.hourly_rate)}/hour</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            After Hours Rate
                          </label>
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">
                              {employeeConfig.after_hours_rate 
                                ? `${formatCurrency(employeeConfig.after_hours_rate)}/hour`
                                : `${formatCurrency(employeeConfig.hourly_rate)}/hour (same as regular)`
                              }
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>Joined: {formatDate(employee.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : activeTab === 'hours' ? (
                  // My Hours Tab Content
                  <div className="space-y-6">
                    {/* Filter Section */}
                    <div className="flex items-center justify-end">
                      <select
                        value={hoursFilter}
                        onChange={(e) => setHoursFilter(e.target.value)}
                        className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                        style={{ 
                          backgroundColor: '#7c3aed',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                          backgroundPosition: 'right 6px center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
                      >
                        <option value="current_month" style={{ backgroundColor: 'white', color: 'black' }}>Current Month</option>
                        <option value="last_month" style={{ backgroundColor: 'white', color: 'black' }}>Last Month</option>
                        <option value="last_3_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 3 Months</option>
                        <option value="last_6_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 6 Months</option>
                        <option value="last_year" style={{ backgroundColor: 'white', color: 'black' }}>Last 1 Year</option>
                      </select>
                    </div>

                    {/* Hours List */}
                    {timeEntries && timeEntries.length > 0 ? (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="grid grid-cols-4 gap-4 text-sm font-medium text-gray-700">
                            <div>Date</div>
                            <div>Clock In</div>
                            <div>Clock Out</div>
                            <div>Total Hours</div>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {timeEntries.map((entry, index) => (
                            <div key={entry.id || index} className="px-6 py-4 hover:bg-gray-50">
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div className="text-gray-900">
                                  {formatDate(entry.clock_in_time)}
                                </div>
                                <div className="text-gray-700">
                                  {formatTime(entry.clock_in_time)}
                                </div>
                                <div className="text-gray-700">
                                  {entry.clock_out_time ? formatTime(entry.clock_out_time) : (
                                    <span className="text-theme-blue font-medium">Still Working</span>
                                  )}
                                </div>
                                <div className="font-medium text-theme-blue">
                                  {entry.total_hours ? formatDuration(entry.total_hours) : (
                                    <span className="text-gray-500">In Progress</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Time Entries Found</h3>
                        <p className="text-gray-600">
                          No completed work hours found for the selected period.
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'paystubs' ? (
                  // My Paystubs Tab Content
                  <div className="space-y-6">
                    <div className="flex items-center justify-end">
                      <select
                        value={paystubsFilter}
                        onChange={(e) => setPaystubsFilter(e.target.value)}
                        className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                        style={{ 
                          backgroundColor: '#7c3aed',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                          backgroundPosition: 'right 6px center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
                      >
                        <option value="current_month" style={{ backgroundColor: 'white', color: 'black' }}>Current Month</option>
                        <option value="last_month" style={{ backgroundColor: 'white', color: 'black' }}>Last Month</option>
                        <option value="last_3_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 3 Months</option>
                        <option value="last_6_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 6 Months</option>
                        <option value="last_year" style={{ backgroundColor: 'white', color: 'black' }}>Last 1 Year</option>
                      </select>
                    </div>

                    {paystubs && paystubs.length > 0 ? (
                      <div className="space-y-4">
                        {paystubs.map((paystub, index) => {
                          // Extract this employee's specific data from the paystub
                          const paystubData = paystub.paystub_data || {};
                          const employeePaystubs = paystubData.employee_paystubs || [];
                          const currentEmployeeData = employeePaystubs.find(emp => emp.user_id === user.id);
                          
                          return (
                            <div key={paystub.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <FileText className="h-5 w-5 text-green-500" />
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-900">
                                        {paystub.agent_name || 'Paystub Report'}
                                      </h4>
                                      <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                                        <span>Generated: {formatDate(paystub.published_at || paystub.created_at)}</span>
                                        {paystubData.start_date && paystubData.end_date && (
                                          <span>
                                            Pay Period: {formatDate(paystubData.start_date)} - {formatDate(paystubData.end_date)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {currentEmployeeData && (
                                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">Total Hours:</span>
                                        <p className="font-medium">{currentEmployeeData.total_hours?.toFixed(2) || '0.00'}h</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Gross Pay:</span>
                                        <p className="font-medium">${currentEmployeeData.gross_pay?.toFixed(2) || '0.00'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Deductions:</span>
                                        <p className="font-medium text-red-600">-${currentEmployeeData.total_deductions?.toFixed(2) || '0.00'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Net Pay:</span>
                                        <p className="font-medium text-green-600">${currentEmployeeData.net_pay?.toFixed(2) || '0.00'}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="ml-4 flex items-center space-x-2">
                                  <button
                                    onClick={() => navigate(`/paystub-report/${paystub.id}?from=employee-profile&employee=${user.id}`)}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-theme-blue hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View Details
                                  </button>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Published
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Paystubs Available</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          No paystubs have been generated for you yet. Paystubs will appear here once they are created by paystub agents.
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'timesheets' ? (
                  // My Timesheets Tab Content - Employee-specific data only
                  <div className="space-y-6">
                    <div className="flex items-center justify-end">
                      <select
                        value={timesheetsFilter}
                        onChange={(e) => setTimesheetsFilter(e.target.value)}
                        className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                        style={{ 
                          backgroundColor: '#7c3aed',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                          backgroundPosition: 'right 6px center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
                      >
                        <option value="current_month" style={{ backgroundColor: 'white', color: 'black' }}>Current Month</option>
                        <option value="last_month" style={{ backgroundColor: 'white', color: 'black' }}>Last Month</option>
                        <option value="last_3_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 3 Months</option>
                        <option value="last_6_months" style={{ backgroundColor: 'white', color: 'black' }}>Last 6 Months</option>
                        <option value="last_year" style={{ backgroundColor: 'white', color: 'black' }}>Last 1 Year</option>
                      </select>
                    </div>

                    {timesheets && timesheets.length > 0 ? (
                      <div className="space-y-6">
                        {timesheets.map((timesheet, index) => {
                          // Extract this employee's specific data from the timesheet
                          const timesheetData = timesheet.timesheet_data || {};
                          const employeeData = timesheetData.employee_data || [];
                          const currentEmployeeData = employeeData.find(emp => emp.user_id === user.id);
                          
                          if (!currentEmployeeData) return null;

                          return (
                            <div key={timesheet.id || index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                              {/* Header with Pay Period */}
                              <div className="flex items-start justify-between mb-6">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                      Pay Period
                                    </span>
                                    <span className="text-sm text-gray-900">
                                      {timesheetData.start_date && timesheetData.end_date && 
                                        `${formatDate(timesheetData.start_date)} to ${formatDate(timesheetData.end_date)}`
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Employee's Daily Hours Table - Direct display */}
                              <div>
                                {currentEmployeeData.daily_entries && currentEmployeeData.daily_entries.length > 0 ? (
                                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Pay</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {currentEmployeeData.daily_entries.map((entry, dayIndex) => {
                                            const entryDate = new Date(entry.date);
                                            const dayName = entryDate.toLocaleDateString('en-US', { weekday: 'short' });
                                            const dailyPay = ((entry.regular_hours || 0) * (currentEmployeeData.hourly_rate || 0)) + 
                                                           ((entry.after_hours || 0) * (currentEmployeeData.after_hours_rate || 0));
                                            
                                            return (
                                              <tr key={dayIndex} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                  {formatDate(entry.date)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                  {dayName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                                                    {entry.regular_hours?.toFixed(2) || '0.00'}h
                                                  </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700">
                                                    {entry.after_hours?.toFixed(2) || '0.00'}h
                                                  </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-theme-blue">
                                                  {entry.total_hours?.toFixed(2) || '0.00'}h
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                                  ${dailyPay.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        {/* Summary Row */}
                                        <tfoot className="bg-blue-50">
                                          <tr>
                                            <td className="px-6 py-3 text-sm font-bold text-blue-900" colSpan="2">Total:</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                                              {currentEmployeeData.regular_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-orange-600">
                                              {currentEmployeeData.after_hours_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-theme-blue">
                                              {currentEmployeeData.total_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                                              ${currentEmployeeData.total_pay?.toFixed(2) || '0.00'}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                                    <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">No daily entries found for this period</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Timesheets Available</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          No published timesheets found for you yet. Your timesheet records will appear here once they are published.
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'taxes' ? (
                  // My Taxes Tab Content - Read-only tax settings summary
                  <div className="space-y-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin h-6 w-6 border-4 border-theme-blue border-t-transparent rounded-full"></div>
                        <span className="ml-3 text-sm text-gray-600">Loading tax settings...</span>
                      </div>
                    ) : taxSettings ? (
                      <div className="space-y-4">
                        {/* Employment Classification */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Employment Classification</h4>
                          <div className="flex items-center space-x-2">
                            <div className={`px-3 py-1.5 text-sm rounded-lg border-2 ${
                              taxSettings.employment_classification === 'w2' 
                                ? 'border-theme-blue bg-blue-50 text-blue-700' 
                                : 'border-gray-200 bg-gray-50 text-gray-600'
                            }`}>
                              <span className="font-medium">
                                {taxSettings.employment_classification === 'w2' ? '✓ W2 Employee' : 'W2 Employee'}
                              </span>
                            </div>
                            <div className={`px-3 py-1.5 text-sm rounded-lg border-2 ${
                              taxSettings.employment_classification === '1099' 
                                ? 'border-orange-500 bg-orange-50 text-orange-700' 
                                : 'border-gray-200 bg-gray-50 text-gray-600'
                            }`}>
                              <span className="font-medium">
                                {taxSettings.employment_classification === '1099' ? '✓ 1099 Contractor' : '1099 Contractor'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Address Information */}
                        {(taxSettings.street_address || taxSettings.city || taxSettings.state) && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Residence Address</h4>
                            <div className="space-y-2 text-sm text-gray-700">
                              {taxSettings.street_address && (
                                <p>{taxSettings.street_address}</p>
                              )}
                              <p>
                                {taxSettings.city && taxSettings.city}{taxSettings.city && taxSettings.state && ', '}
                                {taxSettings.state && stateNames[taxSettings.state]} {taxSettings.zip_code}
                              </p>
                            </div>
                            {taxSettings.state && (
                              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-800">
                                  <strong>State Tax Rate:</strong> {taxSettings.state_tax_rate || 0}%
                                  {parseFloat(taxSettings.state_tax_rate || 0) === 0 && ' (No state income tax)'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Tax Withholding Information */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Tax Withholding Settings</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Filing Status</label>
                              <p className="mt-1 text-sm text-gray-900">
                                {filingStatusLabels[taxSettings.filing_status] || taxSettings.filing_status || 'Not specified'}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Federal Allowances</label>
                              <p className="mt-1 text-sm text-gray-900">{taxSettings.federal_allowances || 1}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">State Allowances</label>
                              <p className="mt-1 text-sm text-gray-900">{taxSettings.state_allowances || 1}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Number of Dependents</label>
                              <p className="mt-1 text-sm text-gray-900">{taxSettings.number_of_dependents || 0}</p>
                            </div>
                          </div>
                        </div>

                        {/* Tax Deductions Status */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Tax Deductions Status</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-900">Federal Income Tax</span>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  taxSettings.enable_federal_tax 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {taxSettings.enable_federal_tax ? 'Enabled' : 'Disabled'}
                                </span>
                                {taxSettings.enable_federal_tax && (
                                  <span className="text-xs text-gray-600">{taxSettings.federal_tax_rate || 12}%</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-900">State Income Tax</span>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  taxSettings.enable_state_tax 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {taxSettings.enable_state_tax ? 'Enabled' : 'Disabled'}
                                </span>
                                {taxSettings.enable_state_tax && (
                                  <span className="text-xs text-gray-600">{taxSettings.state_tax_rate || 0}%</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-900">Social Security</span>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  taxSettings.enable_social_security 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {taxSettings.enable_social_security ? 'Enabled' : 'Disabled'}
                                </span>
                                {taxSettings.enable_social_security && (
                                  <span className="text-xs text-gray-600">6.2%</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-900">Medicare</span>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  taxSettings.enable_medicare 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {taxSettings.enable_medicare ? 'Enabled' : 'Disabled'}
                                </span>
                                {taxSettings.enable_medicare && (
                                  <span className="text-xs text-gray-600">1.45%</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Additional Withholdings */}
                        {(parseFloat(taxSettings.additional_federal_withholding) > 0 || parseFloat(taxSettings.additional_state_withholding) > 0) && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Withholdings</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {parseFloat(taxSettings.additional_federal_withholding) > 0 && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Additional Federal</label>
                                  <p className="mt-1 text-sm text-gray-900">${parseFloat(taxSettings.additional_federal_withholding).toFixed(2)} per paycheck</p>
                                </div>
                              )}
                              {parseFloat(taxSettings.additional_state_withholding) > 0 && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">Additional State</label>
                                  <p className="mt-1 text-sm text-gray-900">${parseFloat(taxSettings.additional_state_withholding).toFixed(2)} per paycheck</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* YTD Information */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Year-to-Date (YTD) Totals</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD Gross Pay</p>
                              <p className="text-lg font-bold text-theme-blue">${parseFloat(taxSettings.ytd_gross_pay || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD Federal Tax</p>
                              <p className="text-lg font-bold text-red-600">${parseFloat(taxSettings.ytd_federal_tax || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-3 bg-orange-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD State Tax</p>
                              <p className="text-lg font-bold text-orange-600">${parseFloat(taxSettings.ytd_state_tax || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD Social Security</p>
                              <p className="text-lg font-bold text-green-600">${parseFloat(taxSettings.ytd_social_security || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD Medicare</p>
                              <p className="text-lg font-bold text-purple-600">${parseFloat(taxSettings.ytd_medicare || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700">YTD Other Deductions</p>
                              <p className="text-lg font-bold text-gray-600">${parseFloat(taxSettings.ytd_other_deductions || 0).toFixed(2)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Exemptions */}
                        {(taxSettings.exempt_from_federal || taxSettings.exempt_from_state) && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-yellow-800 mb-2">Tax Exemptions</h4>
                            <div className="space-y-2">
                              {taxSettings.exempt_from_federal && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-sm text-yellow-800">Exempt from Federal Income Tax Withholding</span>
                                </div>
                              )}
                              {taxSettings.exempt_from_state && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-sm text-yellow-800">Exempt from State Income Tax Withholding</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tax Settings Found</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          Your tax settings haven't been configured yet. Please contact your manager or HR administrator to set up your tax information.
                        </p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyDashboard;